import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, InputAdornment, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PropTypes from "prop-types";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import { isDraftEvent } from "../../../../components/Events/Event/BasicInfo/paymentStateUtils";
import CreateForm, {
  EVENT_FORM_STEP_IDS,
} from "../../../../components/Events/Form/Form";
import {
  UIButton,
  UIModal,
  UIIcon,
  UISelect,
  UIActionModal,
} from "../../../../components/ui";
import * as adminEventsApi from "../../../../network/apis/events";
import * as companiesApi from "../../../../network/apis/companies";
import * as lookupsApi from "../../../../network/apis/lookups";
import {
  getLookupList,
  mapLookupRowsToOptions,
  useScheduleBounds,
} from "../../../Company-portal/Events/_eventFormHelpers";
import {
  COMPANY_PORTAL_FORM_VARIANT,
  getPortalApiErrorMessage,
  isCompanyPortalEventReadOnly,
  mergePortalEventTypeIntoPayload,
  unwrapPortalEventEnvelope,
} from "../../../../utils/companyPortal/eventApi";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  formatEventStatusTabLabel,
  showErrorMsg,
  showSuccessMsg,
} from "../../../../utils/Helpers";
import { eventStatuses, lookupsKeys } from "../../../../utils/Constants";

import "./CreateEventV2.scss";

const ADMIN_EVENT_DRAFT_ID_KEY = "adminCreateEventDraftId";
const ADMIN_EVENT_COMPANY_ID_KEY = "adminCreateEventCompanyId";
const COMPANY_SEARCH_DEBOUNCE_MS = 700;
const ADMIN_DRAFT_EVENT_STEP_IDS = [
  EVENT_FORM_STEP_IDS.basicInfo,
  EVENT_FORM_STEP_IDS.crewSize,
  EVENT_FORM_STEP_IDS.rolesShifts,
  EVENT_FORM_STEP_IDS.zonesRequirements,
];

const defaultLabels = {
  modalTitle: "Create Event",
  modalSubtitle: "Select Client Company",
  modalDescription:
    "Select the company this event will be created for before starting the event wizard.",
  companyFieldLabel: "Select Target Company",
  companyFieldPlaceholder: "Search by company name or ID",
  registeredCompanies: "{count} registered companies",
  warningTitle: "Company context",
  warningMessage:
    "Creating an event as a Super Admin will associate it with the selected company. All company-specific rules and e-wallet balances will apply.",
  companyRequiredError: "Select a company before creating the event",
  cancel: "Cancel",
  next: "Next: Basic Info",
};

const safeSessionSet = (key, value) => {
  if (!value) return;
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch (_e) {
    // sessionStorage can be unavailable in private browsing.
  }
};

const safeSessionGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (_e) {
    return null;
  }
};

const safeSessionRemove = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (_e) {
    // no-op
  }
};

const normalizeCompanyName = (row, lang) => {
  const localized = lang === "ar" ? row?.name_ar : row?.name_en;
  return (
    localized ||
    row?.name_en ||
    row?.name_ar ||
    row?.name ||
    row?.company_name ||
    ""
  );
};

const normalizeCompanyOption = (row, lang) => ({
  ...row,
  id: row?.id,
  name: normalizeCompanyName(row, lang) || String(row?.id ?? ""),
  code: row?.company_code ?? row?.reference_code ?? row?.slug ?? row?.id,
});

const resolveAdminEvent = (response) => {
  const unwrapped = unwrapPortalEventEnvelope(response);
  if (unwrapped) return unwrapped;
  const node = response?.data?.data;
  if (!node) return null;
  return {
    id: node.id != null ? String(node.id) : null,
    attributes: node,
    raw: node,
  };
};

const useAdminEventLookups = () => {
  const lang = useSelector((state) => state.locale.lang);
  const [requirementsOptions, setRequirementsOptions] = useState([]);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);

  // `lang` is intentionally a dep here: the backend returns requirement/role
  // names localized to the request's `locale` header, so this catalog is
  // stale after a language switch unless re-fetched.
  useEffect(() => {
    let cancelled = false;

    lookupsApi
      .getLookups([
        lookupsKeys.requirements,
        lookupsKeys.roles,
        lookupsKeys.eventTypes,
      ])
      .then((res) => {
        if (cancelled) return;
        const requirementsList = getLookupList(res, lookupsKeys.requirements);
        const rolesList = getLookupList(res, lookupsKeys.roles);
        const eventTypesList = getLookupList(res, lookupsKeys.eventTypes);
        setRequirementsOptions(requirementsList);
        setRolesOptions(mapLookupRowsToOptions(rolesList));
        setEventTypeOptions(eventTypesList);
      })
      .catch(() => {
        if (!cancelled) {
          setRequirementsOptions([]);
          setRolesOptions([]);
          setEventTypeOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return { requirementsOptions, rolesOptions, eventTypeOptions };
};

const CompanyOption = ({ option }) => (
  <div className="admin-create-event-company-option">
    <span className="admin-create-event-company-option__name">{option.name}</span>
    <span className="admin-create-event-company-option__meta">
      ID: {option.code}
    </span>
  </div>
);

CompanyOption.propTypes = {
  option: PropTypes.shape({
    name: PropTypes.string,
    code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const CreateEventV2 = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: routeEventId } = useParams();
  const lang = useSelector((state) => state.locale.lang);
  const shared = messages[lang]?.shared ?? messages.en.shared;
  const eventsCatalog = messages[lang]?.events ?? messages.en.events;
  const labels =
    messages[lang]?.events?.creatEvent?.superAdminCreate || defaultLabels;

  const isEditMode = Boolean(routeEventId);
  const eventIdRef = useRef(null);
  const isPageReloadingRef = useRef(false);
  /** Kept out of the restore-draft effect's deps so a language switch doesn't re-trigger it. */
  const generalErrMsgRef = useRef(shared.generalErrMsg);
  generalErrMsgRef.current = shared.generalErrMsg;
  const eventsCatalogRef = useRef(eventsCatalog);
  eventsCatalogRef.current = eventsCatalog;
  const [initialEvent, setInitialEvent] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyFieldTouched, setCompanyFieldTouched] = useState(false);
  // committedCompanyId tracks the company the user has explicitly confirmed by
  // clicking "Next". It is separate from selectedCompany (the dropdown value)
  // so that merely selecting a company in the dropdown does NOT dismiss the modal.
  const [committedCompanyId, setCommittedCompanyId] = useState(
    () =>
      new URLSearchParams(location.search).get("company_id") ||
      safeSessionGet(ADMIN_EVENT_COMPANY_ID_KEY) ||
      null,
  );
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [nextLoading, setNextLoading] = useState(false);

  // --- Conflict-check confirmation modal (edit mode: upcoming/ongoing events) ---
  const [conflictModal, setConflictModal] = useState({ open: false, message: "" });
  // Holds the resolve function of the Promise that saveEventBodyChecked is awaiting
  // while the conflict modal is open. Resolved with true (confirm) or false (cancel).
  const conflictResolverRef = useRef(null);

  const { rolesOptions, requirementsOptions, eventTypeOptions } =
    useAdminEventLookups();
  const { scheduleBounds, updateScheduleBounds } = useScheduleBounds();

  // selectedCompanyId is used for API calls only after the user has committed.
  const selectedCompanyId =
    committedCompanyId ||
    initialEvent?.company_id ||
    initialEvent?.company?.id;

  // The company-selection modal stays open until the user explicitly clicks
  // "Next". It must NOT close just because the dropdown value changed.
  const selectionOpen = !isEditMode && !selectedCompanyId;
  // The crew size (and a custom plan) is set while the event is a draft, or on an event
  // published before the payment flow that has no crew size yet.
  const isDraft = isEditMode
    ? isDraftEvent(initialEvent) || (Boolean(initialEvent) && !initialEvent.crew_size_plan)
    : true;

  const syncUrlParam = useCallback(
    (key, value) => {
      if (!value) return;
      const search = new URLSearchParams(location.search);
      if (search.get(key) === String(value)) return;
      search.set(key, String(value));
      history.replace({
        pathname: location.pathname,
        search: `?${search.toString()}`,
      });
    },
    [history, location.pathname, location.search],
  );

  const loadCompanies = useCallback(
    async (query = "") => {
      setCompaniesLoading(true);
      try {
        const params = {
          page: 1,
          items: 200,
          status: "approved",
        };
        const q = query.trim();
        if (q.length >= 2) params.name_or_id = q;
        const res = await companiesApi.getCompanies(params);
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCompanyOptions(rows.map((row) => normalizeCompanyOption(row, lang)));
        setCompaniesTotal(Number(res?.headers?.["total-count"] || rows.length));
      } catch (e) {
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        setCompanyOptions([]);
        setCompaniesTotal(0);
      } finally {
        setCompaniesLoading(false);
      }
    },
    [lang, shared.generalErrMsg],
  );

  useEffect(() => {
    if (!selectionOpen) return undefined;
    const timer = window.setTimeout(() => {
      void loadCompanies(companySearch);
    }, COMPANY_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [companySearch, loadCompanies, selectionOpen]);

  useEffect(() => {
    const companyId = new URLSearchParams(location.search).get("company_id");
    if (companyId) {
      safeSessionSet(ADMIN_EVENT_COMPANY_ID_KEY, companyId);
    }
  }, [location.search]);

  const saveEventBody = useCallback(
    async (body) => {
      const companyId = selectedCompanyId;
      if (!companyId) {
        showErrorMsg(labels.companyRequiredError);
        return null;
      }

      const payload = mergePortalEventTypeIntoPayload(
        {
          ...(body && typeof body === "object" ? body : {}),
          company_id: companyId,
        },
        initialEvent,
      );

      if (!eventIdRef.current) {
        const res = await adminEventsApi.addEvent({ data: payload });
        const unwrapped = resolveAdminEvent(res);
        const id = unwrapped?.id;
        if (!id) {
          throw new Error(getPortalApiErrorMessage(null, shared.generalErrMsg));
        }
        eventIdRef.current = id;
        safeSessionSet(ADMIN_EVENT_DRAFT_ID_KEY, id);
        syncUrlParam("event_id", id);
        updateScheduleBounds(unwrapped?.attributes ?? {}, payload);
        setInitialEvent((current) => ({
          ...(current || {}),
          ...(unwrapped?.attributes || {}),
          id,
        }));
        return unwrapped;
      }

      const existing = eventIdRef.current;
      const res = await adminEventsApi.editEvent({
        id: existing,
        data: payload,
      });
      const unwrapped = resolveAdminEvent(res);
      updateScheduleBounds(unwrapped?.attributes ?? {}, payload);
      setInitialEvent((current) => ({
        ...(current || {}),
        ...(unwrapped?.attributes || {}),
        id: existing,
      }));
      return unwrapped ?? { id: existing, attributes: {} };
    },
    [
      initialEvent,
      labels.companyRequiredError,
      selectedCompanyId,
      shared.generalErrMsg,
      syncUrlParam,
      updateScheduleBounds,
    ],
  );

  // Checks for conflicts before saving.
  //
  // Bug-fix (Case 1): only call the conflict API when the payload actually carries
  // date fields. Saving step 2 (roles) sends no dates — passing undefined params
  // caused the backend to use the stored event dates and incorrectly flag PENDING
  // applicants from a non-overlapping event as conflicts.
  //
  // Bug-fix (Case 2): the modal is now resolved via a Promise that keeps the
  // calling handler's await chain alive. The handler returns the saved entity
  // (or null on cancel) so the form can advance / navigate correctly.
  const saveEventBodyChecked = useCallback(
    async (body) => {
      const id = eventIdRef.current;
      const hasDateChanges =
        body?.building_start_at != null ||
        body?.dismantling_end_at != null ||
        body?.event_start_at != null ||
        body?.event_end_at != null;

      const shouldValidate =
        isEditMode &&
        id &&
        [eventStatuses.upcoming, eventStatuses.ongoing].includes(
          initialEvent?.status,
        ) &&
        hasDateChanges;

      if (shouldValidate) {
        try {
          const conflictRes = await adminEventsApi.checkEventConflicts({
            id,
            params: {
              building_start_at: body?.building_start_at,
              dismantling_end_at: body?.dismantling_end_at,
              event_start_at: body?.event_start_at,
              event_end_at: body?.event_end_at,
            },
          });
          const conflictCount =
            conflictRes?.data?.number_of_conflicting_resources ?? 0;
          if (conflictCount > 0) {
            const confirmed = await new Promise((resolve) => {
              conflictResolverRef.current = resolve;
              setConflictModal({ open: true, message: conflictRes.data.message || "" });
            });
            if (!confirmed) return null;
            return saveEventBody({
              ...(body ?? {}),
              expected_number_of_conflicting_resources: conflictCount,
            });
          }
        } catch (e) {
          showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
          return null;
        }
      }

      return saveEventBody(body);
    },
    [isEditMode, initialEvent?.status, saveEventBody, shared.generalErrMsg],
  );

  const handleConflictConfirm = useCallback(() => {
    setConflictModal({ open: false, message: "" });
    conflictResolverRef.current?.(true);
    conflictResolverRef.current = null;
  }, []);

  const handleConflictCancel = useCallback(() => {
    setConflictModal({ open: false, message: "" });
    conflictResolverRef.current?.(false);
    conflictResolverRef.current = null;
  }, []);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const urlEventId = search.get("event_id");
    const storedEventId = routeEventId
      ? null
      : safeSessionGet(ADMIN_EVENT_DRAFT_ID_KEY);
    const eventId = routeEventId || urlEventId || storedEventId;
    if (!eventId) return undefined;

    let cancelled = false;
    eventIdRef.current = eventId;
    if (!routeEventId) {
      safeSessionSet(ADMIN_EVENT_DRAFT_ID_KEY, eventId);
      syncUrlParam("event_id", eventId);
    }

    adminEventsApi
      .getEvent(eventId)
      .then((res) => {
        if (cancelled) return;
        const unwrapped = resolveAdminEvent(res);
        const attrs = unwrapped?.attributes ?? null;

        if (
          routeEventId &&
          attrs &&
          isCompanyPortalEventReadOnly(attrs.status)
        ) {
          const statusLabel = formatEventStatusTabLabel(
            attrs.status,
            eventsCatalogRef.current.listEvents.eventTabs,
          );
          const msg =
            eventsCatalogRef.current.editEvents.readOnlyEventCannotEditStatus.replace(
              "{status}",
              statusLabel || String(attrs.status ?? ""),
            );
          showErrorMsg(msg);
          history.replace(
            ROUTE_PATHS.viewEvent.replace(
              ":id",
              String(unwrapped?.id ?? routeEventId),
            ),
          );
          return;
        }

        setInitialEvent(attrs);
        updateScheduleBounds(attrs ?? {}, {});
        const companyId = attrs?.company_id ?? attrs?.company?.id;
        if (companyId) {
          safeSessionSet(ADMIN_EVENT_COMPANY_ID_KEY, companyId);
          if (!routeEventId) {
            syncUrlParam("company_id", companyId);
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        eventIdRef.current = null;
        safeSessionRemove(ADMIN_EVENT_DRAFT_ID_KEY);
        showErrorMsg(getPortalApiErrorMessage(e, generalErrMsgRef.current));
      });

    return () => {
      cancelled = true;
    };
  }, [history, location.search, routeEventId, syncUrlParam, updateScheduleBounds]);

  useEffect(() => {
    const markPageReloading = () => {
      isPageReloadingRef.current = true;
    };
    window.addEventListener("beforeunload", markPageReloading);
    return () => {
      window.removeEventListener("beforeunload", markPageReloading);
      if (!isPageReloadingRef.current) {
        safeSessionRemove(ADMIN_EVENT_DRAFT_ID_KEY);
        safeSessionRemove(ADMIN_EVENT_COMPANY_ID_KEY);
      }
    };
  }, []);

  const handleSelectCompany = () => {
    if (!selectedCompany?.id) return;
    safeSessionSet(ADMIN_EVENT_COMPANY_ID_KEY, selectedCompany.id);
    syncUrlParam("company_id", selectedCompany.id);
    // Commit the selection — this is the only place that closes the modal.
    setCommittedCompanyId(selectedCompany.id);
  };

  const handleCancelSelection = () => {
    safeSessionRemove(ADMIN_EVENT_DRAFT_ID_KEY);
    safeSessionRemove(ADMIN_EVENT_COMPANY_ID_KEY);
    history.push(ROUTE_PATHS.events);
  };

  const handlePersistDraft = useCallback(
    async ({ payload, onError }) => {
      if (!payload) return null;
      setDraftLoading(true);
      try {
        const persisted = await saveEventBodyChecked(payload);
        if (persisted) {
          if (isEditMode) {
            showSuccessMsg("eventUpdatedSuccessfully", false);
          }
          const id = eventIdRef.current;
          queueMicrotask(() => {
            history.push(
              isEditMode && id
                ? ROUTE_PATHS.viewEvent.replace(":id", id)
                : ROUTE_PATHS.events,
            );
          });
        }
        return persisted;
      } catch (e) {
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        onError?.(e);
        return null;
      } finally {
        setDraftLoading(false);
      }
    },
    [history, isEditMode, saveEventBodyChecked, shared.generalErrMsg],
  );

  const handleAdvanceAfterValidatedStep = useCallback(
    async ({ stepIndex, payload, onError }) => {
      if (stepIndex > 0 && !eventIdRef.current) {
        showErrorMsg(shared.generalErrMsg);
        return false;
      }
      setNextLoading(true);
      try {
        return await saveEventBodyChecked(payload ?? {});
      } catch (e) {
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        onError?.(e);
        return null;
      } finally {
        setNextLoading(false);
      }
    },
    [saveEventBodyChecked, shared.generalErrMsg],
  );

  const handleFinishPublish = useCallback(
    async ({ payload }) => {
      setNextLoading(true);
      try {
        const persisted = await saveEventBodyChecked(payload ?? {});
        if (!persisted) return false;
        const id = persisted?.id ?? eventIdRef.current;
        if (!id) { showErrorMsg(shared.generalErrMsg); return false; }
        if (!isEditMode || initialEvent?.status === eventStatuses.draft) {
          await adminEventsApi.publishEvent(id);
          showSuccessMsg("eventPublishedSuccessfully", false);
        } else {
          showSuccessMsg("eventUpdatedSuccessfully", false);
        }
        safeSessionRemove(ADMIN_EVENT_DRAFT_ID_KEY);
        safeSessionRemove(ADMIN_EVENT_COMPANY_ID_KEY);
        queueMicrotask(() => {
          history.push(
            isEditMode ? ROUTE_PATHS.viewEvent.replace(":id", id) : ROUTE_PATHS.events,
          );
        });
        return true;
      } catch (e) {
        showErrorMsg(getPortalApiErrorMessage(e, shared.generalErrMsg));
        return false;
      } finally {
        setNextLoading(false);
      }
    },
    [history, initialEvent?.status, isEditMode, saveEventBodyChecked, shared.generalErrMsg],
  );

  const handleCancel = useCallback(() => {
    safeSessionRemove(ADMIN_EVENT_DRAFT_ID_KEY);
    safeSessionRemove(ADMIN_EVENT_COMPANY_ID_KEY);
    const id = routeEventId || eventIdRef.current;
    history.push(
      isEditMode && id
        ? ROUTE_PATHS.viewEvent.replace(":id", id)
        : ROUTE_PATHS.events,
    );
  }, [history, isEditMode, routeEventId]);

  const selectedCompanyValue = useMemo(
    () =>
      companyOptions.find(
        (option) => String(option.id) === String(selectedCompany?.id),
      ) || selectedCompany,
    [companyOptions, selectedCompany],
  );

  const breadcrumbList = useMemo(() => {
    const listEvents = eventsCatalog?.listEvents ?? {};
    const eventsListPath = ROUTE_PATHS.events;

    if (!isEditMode) {
      return [
        {
          label: listEvents.listEventsBreadcrumb,
          path: eventsListPath,
        },
        {
          label: listEvents.createEvent,
          path: "",
        },
      ];
    }

    const eventId = routeEventId || eventIdRef.current;
    const status = initialEvent?.status;

    return [
      {
        label: listEvents.listEventsBreadcrumb,
        path: eventsListPath,
      },
      {
        label: formatEventStatusTabLabel(status, listEvents.eventTabs ?? {}),
        path: status ? `${eventsListPath}?tab=${status}` : eventsListPath,
      },
      {
        label: initialEvent?.name || "",
        path: eventId
          ? ROUTE_PATHS.viewEvent.replace(":id", eventId)
          : eventsListPath,
      },
      {
        label: eventsCatalog?.editEvents?.editEventBreadcrumb,
        path: "",
      },
    ];
  }, [eventsCatalog, initialEvent, isEditMode, routeEventId]);

  return (
    <div className="admin-create-event-page">
      <Breadcrumbs
        list={breadcrumbList}
        className="admin-create-event-page__breadcrumbs"
        BreadCrumbHasBackIcon={false}
      />

      <UIModal
        open={selectionOpen}
        onClose={handleCancelSelection}
        maxWidth="sm"
        PaperProps={{ className: "admin-create-event-company-dialog" }}
      >
        <div className="admin-create-event-company-modal">
          <div className="admin-create-event-company-modal__header">
            <div className="admin-create-event-company-modal__icon-tile">
              <UIIcon
                name="companyBuilding"
                color="#31237D"
                width={24}
                height={24}
                decorative
              />
            </div>
            <div className="admin-create-event-company-modal__title-group">
              <h2>{labels.modalTitle}</h2>
              <p>{labels.modalSubtitle}</p>
            </div>
          </div>

          <div className="admin-create-event-company-modal__body">
            <div className="admin-create-event-company-modal__field-header">
              <span className="label-company">
                <span>{labels.companyFieldLabel}{" "}</span>
                <span className="text-danger">*</span>
              </span>
              <span className="counter-company">
                {labels.registeredCompanies.replace("{count}", companiesTotal)}
              </span>
            </div>

            <UISelect
              className="admin-create-event-company-modal__select"
              placeholder={labels.companyFieldPlaceholder}
              options={companyOptions}
              value={selectedCompanyValue}
              loading={companiesLoading}
              size="large"
              onInputChange={(_event, value, reason) => {
                if (reason === "input") setCompanySearch(value);
              }}
              onChange={(option) => setSelectedCompany(option)}
              onBlur={() => setCompanyFieldTouched(true)}
              error={companyFieldTouched && !selectedCompany?.id}
              helperText={
                companyFieldTouched && !selectedCompany?.id
                  ? labels.companyRequiredError
                  : undefined
              }
              filterOptions={(options) => options}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name || ""
              }
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <CompanyOption option={option} />
                </Box>
              )}
              textFieldProps={{
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  ...(companiesLoading
                    ? { endAdornment: <CircularProgress size={18} /> }
                    : {}),
                },
              }}
            />

            <div className="admin-create-event-company-modal__warning">
              <UIIcon
                name="infoCircle"
                color="#E17100"
                width={20}
                height={20}
                decorative
              />
              <p>{labels.warningMessage}</p>
            </div>
          </div>

          <div className="admin-create-event-company-modal__footer">
            <UIButton type="button" variant="outlined" onClick={handleCancelSelection}>
              {labels.cancel}
            </UIButton>
            <UIButton
              type="button"
              appearance="gradient"
              disabled={!selectedCompany?.id}
              onClick={handleSelectCompany}
            >
              {labels.next}
            </UIButton>
          </div>
        </div>
      </UIModal>

      {!selectionOpen && (
        <CreateForm
          variant={COMPANY_PORTAL_FORM_VARIANT}
          isEditMode={isEditMode}
          rolesOptions={rolesOptions}
          requirementsOptions={requirementsOptions}
          supervisorsOptions={[]}
          eventTypeOptions={eventTypeOptions}
          eventScheduleBounds={scheduleBounds}
          initialEvent={initialEvent}
          initialStep={location.state?.initialStep}
          requestsPortal="admin"
          eventCompanyId={selectedCompanyId}
          onPersistDraft={handlePersistDraft}
          onPersistStepContinue={handleAdvanceAfterValidatedStep}
          onFinishPublish={handleFinishPublish}
          onCancel={handleCancel}
          draftLoading={draftLoading}
          nextLoading={nextLoading}
          stepIds={isDraft ? ADMIN_DRAFT_EVENT_STEP_IDS : undefined}
          canSetCustomCrewSize={isDraft}
        />
      )}

      {/* GUARD: conflict confirmation modal — shown when editing upcoming/ongoing events
          whose new dates overlap an ongoing event with shared approved applicants.
          On confirm the save is re-attempted with expected_number_of_conflicting_resources
          so the backend proceeds with auto-cancellation instead of returning a conflict error. */}
      <UIActionModal
        open={conflictModal.open}
        onClose={handleConflictCancel}
        variant="reject"
        iconName="alertCircle"
        showIcon
        title={
          (eventsCatalog?.editEvents?.conflictModalTitle) ||
          "Date Overlap Detected"
        }
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={shared.confirm}
            onCancel={handleConflictCancel}
            onConfirm={handleConflictConfirm}
            confirmVariant="reject"
          />
        }
      >
        <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
          {conflictModal.message || eventsCatalog?.editEvents?.conflictModalMessage}
        </Typography>
      </UIActionModal>
    </div>
  );
};

export default CreateEventV2;
